import React, { useEffect } from 'react';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core/styles';

import { createHookTimeoutState } from 'utils/react';
import DualListBox from 'ui/duallist/DualListBox';
import Paragraph from 'ui/Paragraph';
import Typography from '@material-ui/core/Typography';

import { loadGroups } from './services';

const styles = theme => ({
    buttons: {
        marginBottom: theme.spacing(2),
    },
    button: {
        color: '#fff !important',
        padding: '6px 16px',
        '&:hover': {
            opacity: 0.7,
        },
    },
    buttonRequired: {
        background: '#76ac53',
        '&:hover': {
            background: '#76ac53',
        },
    },
    buttonOptional: {
        background: '#a4a4a4',
        '&:hover': {
            background: '#a4a4a4',
        },
    },
    buttonLoader: {
        marginLeft: theme.spacing(),
        color: '#fff',
    },
    linkLoader: {
        marginLeft: theme.spacing(),
    },
    listItem: {
        fontWeight: 500,
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(),
    },
    groups: {
        color: theme.palette.text.blue,
        fontWeight: 500,
    },
    groupsList: {
        marginBottom: theme.spacing(),
        fontSize: 16,
    },
    groupsLink: {
        cursor: 'pointer',
    },
    groupsDualList: {
        height: 335,
    },
});

const REQUIRED_TYPE = 'required';
const OPTIONAL_TYPE = 'optional';

export default ({ mode, tabs, connector, values, onChange }) => {
    const validate = group => {
        if (group) {
            tabs.unlock();
        } else {
            tabs.lock();
        }
    };
    let sectionCtx = createContext(connector);
    const onChangeGroups = groups => {
        onChange('extra', { groups });
    };
    const onChangeGroup = group => {
        validate(group[0]);
        onChange('extra', { group: group[0] });
    };
    useEffect(() => {
        validate(values.extra.group);
    });
    return (
        <React.Fragment>
            <Paragraph>
                Select the users and groups (optional) to sync from Azure to
                CyberFOX Password Manager.
            </Paragraph>
            <Section
                ctx={sectionCtx(REQUIRED_TYPE)}
                descr="1. Select the Azure group that contains the users to sync to CyberFOX Password Manager"
                editable={mode !== 'details'}
                values={(values.extra.group && [values.extra.group]) || []}
                onChange={onChangeGroup}
            />
            <Section
                ctx={sectionCtx(OPTIONAL_TYPE)}
                descr="2. Optional - Select the Azure groups to sync to CyberFOX Password Manager"
                editable={mode !== 'details'}
                multiple
                values={values.extra.groups || []}
                onChange={onChangeGroups}
            />
        </React.Fragment>
    );
};

function createSectionApi(ctx, name) {
    return {
        open() {
            ctx.open(name);
        },
        update(groups) {
            ctx.update(name, groups);
        },
        get name() {
            return name;
        },
        get disabled() {
            return ctx.isDisabled(name);
        },
        get loading() {
            return ctx.isLoading(name);
        },
        get opened() {
            return ctx.isOpened(name);
        },
        get options() {
            return ctx.options;
        },
    };
}

function createContext(connector) {
    const [state, updateState] = createHookTimeoutState({
        loading: false,
        loaded: false,
        opened: false,
        active: '',
        groups: [],
        options: [],
        selected: {},
    });

    const getGroups = () => {
        if (state.loaded) {
            updateState({ opened: true });
        } else if (state.loading === false) {
            updateState({ loading: true });
            loadGroups(connector.uuid).then(groups => {
                const options = groups.map(group => ({
                    value: group.id,
                    label: group.name,
                }));
                updateState({
                    groups,
                    options,
                    loading: false,
                    loaded: true,
                    opened: true,
                });
            });
        }
    };
    const api = {
        open(name) {
            if (!state.loading && state.active !== name) {
                updateState({ active: name });
                getGroups();
            }
        },
        isDisabled(name) {
            return state.loading;
        },
        isOpened(name) {
            return state.active === name && state.opened;
        },
        isLoading(name) {
            return state.active === name && state.loading;
        },
        isLoaded(name) {
            return state.loaded;
        },

        get options() {
            return state.options;
        },
    };
    return name => createSectionApi(api, name);
}

function mapToOptions(items, options) {
    const buff = [];
    for (const item of items) {
        for (const option of options) {
            if (option.value === item) {
                buff.push({
                    name: option.label,
                    id: option.value,
                });
                break;
            }
        }
    }
    return buff;
}

function Section({ ctx, descr, editable, values, onChange, multiple = false }) {
    const onChangeList = items => onChange(mapToOptions(items, ctx.options));
    const onOpen = () => ctx.open();
    const isEmpty = values.length === 0;
    return (
        <>
            <ListItem>{descr}</ListItem>
            <div>
                {!ctx.opened && isEmpty && (
                    <ProgressButton
                        type={ctx.name}
                        disabled={!editable || ctx.disabled}
                        onClick={onOpen}
                        loading={ctx.loading}
                    />
                )}
                {!ctx.opened && !isEmpty && (
                    <SectionGroups
                        groups={values}
                        disabled={!editable}
                        loading={ctx.loading}
                        onClick={onOpen}
                    />
                )}
                {ctx.opened && (
                    <SectionDualList
                        disabled={!editable}
                        multiple={multiple}
                        options={ctx.options}
                        values={values.map(item => item.id)}
                        onChange={onChangeList}
                    />
                )}
            </div>
        </>
    );
}

const SectionDualList = withStyles(
    styles
)(({ disabled, multiple, values, options, onChange, classes }) => (
    <DualListBox
        className={classes.groupsDualList}
        disabled={disabled}
        multiple={multiple}
        options={options}
        initialValue={values}
        sourceListHeaderName="Available Groups"
        destinationListHeaderName="Selected Groups"
        onChange={onChange}
    />
));

const SectionGroups = withStyles(styles)(
    ({ groups, disabled, loading, onClick, classes }) => (
        <Typography component="div" className={classes.groups}>
            <div className={classes.groupsList}>
                {groups.map(group => (
                    <div>{group.name}</div>
                ))}
            </div>
            {!disabled && (
                <div onClick={onClick} className={classes.groupsLink}>
                    Edit
                    {loading && (
                        <CircularProgress
                            size={12}
                            className={classes.linkLoader}
                        />
                    )}
                </div>
            )}
        </Typography>
    )
);

const ProgressButton = withStyles(styles)(
    ({ type, loading, disabled, onClick, classes }) => {
        const className = classNames(classes.button, {
            [classes.buttonRequired]: type === REQUIRED_TYPE,
            [classes.buttonOptional]: type === OPTIONAL_TYPE,
        });
        return (
            <Button
                className={className}
                disabled={disabled}
                color="secondary"
                onClick={onClick}
            >
                Select
                {loading && (
                    <CircularProgress
                        size={12}
                        className={classes.buttonLoader}
                    />
                )}
            </Button>
        );
    }
);

const ListItem = withStyles(styles)(({ children, classes }) => (
    <Typography variant="body2" className={classes.listItem}>
        {children}
    </Typography>
));
